import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography
} from '@mui/material';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';

function PrefillImageFromUrl({ isEdit, editing, open }) {
    const { values, setFieldValue } = useFormikContext();

    React.useEffect(() => {
        if (!open) return;
        if (isEdit && editing?.image && !values.imageFile) {
            (async () => {
                try {
                    const res = await fetch(editing.image);
                    const blob = await res.blob();
                    const file = new File([blob], 'image.jpg', { type: blob.type });
                    setFieldValue('imageFile', file, false);
                    if (!values.imagePreview) {
                        setFieldValue('imagePreview', editing.image, false);
                    }
                } catch (err) {
                    console.error('Error convirtiendo imagen a File:', err);
                }
            })();
        }
    }, [isEdit, editing, values.imageFile, values.imagePreview, setFieldValue, open]);

    return null;
}

const ModalMatriz = ({ open, onClose, editing, items, setItems, multiple = true }) => {
    const isEdit = Boolean(editing);

    const validationSchema = Yup.object().shape({
        label: Yup.string().trim().required('Campo obligatorio'),
        imageFile: Yup.mixed()
            .nullable()
            .test('image-required', 'La imagen es obligatoria', function (value) {
                return !!this.parent.imagePreview || value instanceof File;
            }),
    });

    const initialValues = {
        id: editing?.id ?? Math.random(),
        label: editing?.label ?? '',
        imagePreview: editing?.image ?? '',
        imageFile: null,
    };

    const handleSubmit = (values, { resetForm }) => {
        const payload = {
            id: values.id,
            label: values.label,
            imageFile: values.imageFile,
            image: values.imagePreview,
        };

        if (multiple) {
            if (isEdit) {
                setItems(prev => prev.map(it => (it.id === values.id ? { ...it, ...payload } : it)));
            } else {
                setItems(prev => [...prev, payload]);
            }
        } else {
            // 👇 Para edición o creación de un solo ítem
            setItems(payload);
        }

        resetForm();
        onClose();
    };

    const handleClose = (resetForm) => {
        resetForm?.();
        onClose();
    };

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Editar punto' : 'Agregar punto'}</DialogTitle>

            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isSubmitting,
                    resetForm,
                }) => (
                    <Form autoComplete="off">
                        <PrefillImageFromUrl isEdit={isEdit} editing={editing} open={open} />

                        <DialogContent>
                            <TextField
                                fullWidth
                                name="label"
                                label="Nombre del item"
                                value={values.label}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.label && Boolean(errors.label)}
                                helperText={touched.label && errors.label}
                                margin="normal"
                            />

                            <Button
                                variant="outlined"
                                fullWidth
                                component="label"
                                sx={{ height: 50, mt: 1 }}
                            >
                                {values.imagePreview ? 'Cambiar imagen' : 'Cargar imagen'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const url = URL.createObjectURL(file);
                                        setFieldValue('imageFile', file);
                                        setFieldValue('imagePreview', url);
                                    }}
                                />
                            </Button>

                            {errors.imageFile && (
                                <Typography color="error" variant="caption">
                                    {errors.imageFile}
                                </Typography>
                            )}

                            {values.imagePreview && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <Box
                                        component="img"
                                        src={values.imagePreview}
                                        alt="preview"
                                        sx={{
                                            maxWidth: '100%',
                                            maxHeight: 220,
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                            boxShadow: 1,
                                        }}
                                    />
                                </Box>
                            )}
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={() => handleClose(resetForm)} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" variant="contained" disabled={isSubmitting}>
                                Guardar
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default ModalMatriz;